<script setup>
/**
 * The one CTA editor, shared by the rail's Content tab and the preview's
 * click-to-edit popover: a type-picker grid (all six contract types), the
 * label, and the per-type payload field (url / phone / text to copy).
 *
 * Edits are emitted as patches; the owner applies them immutably via
 * `applyCtaPatch` so the page's autosave watcher sees every change.
 */
import { useI18n } from 'vue-i18n';

import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import {
  CTA_TYPES,
  CTA_URL_TYPES,
} from 'dashboard/components-next/Campaigns/helpers/campaignContent';

const props = defineProps({
  cta: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update', 'remove']);

const { t } = useI18n();

const CTA_TYPE_ICONS = {
  [CTA_TYPES.SEND_MESSAGE]: 'i-lucide-message-circle',
  [CTA_TYPES.PHONE_CALL]: 'i-lucide-phone',
  [CTA_TYPES.OPEN_LINK]: 'i-lucide-external-link',
  [CTA_TYPES.PAGE_SNIPPET]: 'i-lucide-app-window',
  [CTA_TYPES.COPY_TEXT]: 'i-lucide-copy',
  [CTA_TYPES.DISMISS]: 'i-lucide-x',
};

const typeOptions = Object.entries(CTA_TYPES).map(([key, value]) => ({
  value,
  icon: CTA_TYPE_ICONS[value],
  label: t(`CAMPAIGN.BUILDER.CONTENT.CTAS.TYPE.${key}`),
}));

const needsUrl = () => CTA_URL_TYPES.includes(props.cta.type);

const typeTileClasses = isActive => [
  'flex flex-col items-center justify-center gap-1 py-2 px-1 rounded-lg border text-label-small font-medium transition-colors',
  isActive
    ? 'border-n-violet-8 bg-n-violet-3 text-n-violet-11'
    : 'border-n-weak bg-n-alpha-1 text-n-slate-11 hover:bg-n-alpha-2 hover:text-n-slate-12',
];
</script>

<template>
  <div class="flex flex-col gap-3">
    <div class="flex items-start justify-between gap-2">
      <div
        role="group"
        :aria-label="t('CAMPAIGN.BUILDER.CONTENT.CTAS.TYPE_GROUP_LABEL')"
        class="grid flex-1 grid-cols-3 gap-1.5"
      >
        <button
          v-for="option in typeOptions"
          :key="option.value"
          type="button"
          :class="typeTileClasses(cta.type === option.value)"
          :aria-pressed="cta.type === option.value"
          @click="emit('update', { type: option.value })"
        >
          <Icon :icon="option.icon" class="size-4" />
          {{ option.label }}
        </button>
      </div>
      <Button
        variant="ghost"
        color="ruby"
        size="xs"
        icon="i-lucide-trash"
        :aria-label="t('CAMPAIGN.BUILDER.CONTENT.CTAS.REMOVE')"
        @click="emit('remove')"
      />
    </div>

    <Input
      :model-value="cta.label"
      :label="t('CAMPAIGN.BUILDER.CONTENT.CTAS.LABEL_INPUT.LABEL')"
      :placeholder="t('CAMPAIGN.BUILDER.CONTENT.CTAS.LABEL_INPUT.PLACEHOLDER')"
      @update:model-value="emit('update', { label: $event })"
    />

    <Input
      v-if="needsUrl()"
      :model-value="cta.url"
      type="url"
      :label="t('CAMPAIGN.BUILDER.CONTENT.CTAS.URL_INPUT.LABEL')"
      :placeholder="t('CAMPAIGN.BUILDER.CONTENT.CTAS.URL_INPUT.PLACEHOLDER')"
      @update:model-value="emit('update', { url: $event })"
    />

    <Input
      v-if="cta.type === CTA_TYPES.PHONE_CALL"
      :model-value="cta.phone"
      type="tel"
      :label="t('CAMPAIGN.BUILDER.CONTENT.CTAS.PHONE_INPUT.LABEL')"
      :placeholder="t('CAMPAIGN.BUILDER.CONTENT.CTAS.PHONE_INPUT.PLACEHOLDER')"
      @update:model-value="emit('update', { phone: $event })"
    />

    <Input
      v-if="cta.type === CTA_TYPES.COPY_TEXT"
      :model-value="cta.copyText"
      :label="t('CAMPAIGN.BUILDER.CONTENT.CTAS.COPY_INPUT.LABEL')"
      :placeholder="t('CAMPAIGN.BUILDER.CONTENT.CTAS.COPY_INPUT.PLACEHOLDER')"
      @update:model-value="emit('update', { copyText: $event })"
    />
  </div>
</template>
